'use client';

import { Box, Text, VStack, HStack, Flex, IconButton, Stack } from '@chakra-ui/react';
import { useTranslations } from 'next-intl';
import { useState } from 'react';
import { FiChevronDown, FiChevronUp } from 'react-icons/fi';
import { usePortfolioData } from '@/lib/hooks/usePortfolioData';

import { useUserDetail } from '@/lib/hooks/useUserDetail';
import {
  formatAmount,
  formatPnLDisplay,
  formatCurrency,
  formatHoldingTime,
  formatPercentageDisplay,
} from '@/lib/utils/format';

interface TraderAccountStatsProps {
  address: string;
}

export function TraderAccountStats({ address }: TraderAccountStatsProps) {
  const t = useTranslations('trader.stats');
  const [isExpanded, setIsExpanded] = useState(true);
  const { data: userDetail } = useUserDetail(address);

  const toggleExpanded = () => {
    setIsExpanded(!isExpanded);
  };

  // const totalTrades =
  //   (userDetail?.profitTrades ?? 0) + (userDetail?.lossTrades ?? 0);
  // const profitBarWidth =
  //   totalTrades > 0 ? ((userDetail?.profitTrades ?? 0) / totalTrades) * 100 : 0;
  // const lossBarWidth =
  //   totalTrades > 0 ? ((userDetail?.lossTrades ?? 0) / totalTrades) * 100 : 0;
  // const portfolioData = usePortfolioData(address);
  // // perpweek
  // const totalPnls = portfolioData?.data?.[5]?.[1]?.pnlHistory ?? [];

  // const totalPnl =
  //   totalPnls?.length > 0 ? totalPnls[totalPnls.length - 1][1] : 0;

  // const prevAssets = Number(userDetail?.assets ?? 0) - Number(totalPnl ?? 0);
  // const totalPnlPercentage =
  //   prevAssets !== 0 ? Math.abs(Number(totalPnl) / prevAssets) : 0;

  return (
    <VStack gap={6} p={4} h="full" w="full" border=" 1px solid rgba(255, 255, 255, 0.15)" rounded="16px">
      <Text w="full" fontSize="20px" color="text.t1" fontWeight="700">
        Overview
      </Text>
      {/* Header Section */}
      <Stack 
        direction={{ base: "row", md: 'column' }}
        justify={{ base: "start", md: 'space-between' }}
        align="start"
        wrap="wrap" 
        gap={0} 
        rowGap={{ base: "4", md: '0' }} 
        w="full"
        h="full"
      >
        <Stack 
          width={{ base: "50%", md: '100%' }}
          direction={{ base: "column", md: 'row' }} 
          justify={{ base: "start", md: 'space-between' }}
          align={{ base: "start", md: 'center' }} 
          gap={1} 
        >
          <Text fontSize="sm" color="text.t1/60" fontWeight="medium">
            {t('accountAssets')}
          </Text>
          <Text fontSize="xl" color="text.t1/90" fontWeight="bold">
            {formatCurrency(userDetail?.assets ?? 0)}
          </Text>
        </Stack>

        <Stack 
          width={{ base: "50%", md: '100%' }}
          direction={{ base: "column", md: 'row' }} 
          justify={{ base: "start", md: 'space-between' }}
          align={{ base: "end", md: 'start' }} 
          gap={1} 
        >
          <Text fontSize="sm" color="text.t1/60" fontWeight="medium">
            {t('totalPnl')}
          </Text>
          <VStack align="end">
            <Text
              fontSize="xl"
              fontWeight="bold"
              color={userDetail?.pnl && Number(userDetail?.pnl) >= 0 ? 'profit' : 'loss'}
            >
              {formatPnLDisplay(Number(userDetail?.pnl ?? 0))}
            </Text>
            <Text
              fontSize="xs"
              fontWeight="bold"
              color={userDetail?.pnl && Number(userDetail?.pnl) >= 0 ? 'profit' : 'loss'}
            >
              {formatPercentageDisplay(userDetail?.pnlPercent ?? 0)}
              {/* {totalPnl && Number(totalPnl) >= 0 ? '+' : '-'}
              {formatPercentageDisplay(Number(totalPnlPercentage * 100), {
                signDisplay: 'never',
              })} */}
            </Text>
          </VStack>
        </Stack>

        <Stack 
          width={{ base: "25%", md: '100%' }}
          direction={{ base: "column", md: 'row' }} 
          justify={{ base: "start", md: 'space-between' }}
          align={{ base: "start", md: 'center' }} 
          gap={1} 
        >
          <Text fontSize={{ base: "2xs", md: 'sm' }} color="text.t1/60" fontWeight="medium">
            {t('winRate')}
          </Text>
          <Text fontSize={{ base: "sm", md: 'xl' }} color="text.t1/80" fontWeight="semibold">
            {formatPercentageDisplay(userDetail?.winRate ?? 0, {
              signDisplay: 'auto',
            })}
          </Text>
        </Stack>
        <Stack 
          width={{ base: "25%", md: '100%' }}
          direction={{ base: "column", md: 'row' }} 
          justify={{ base: "start", md: 'space-between' }}
          align={{ base: "center", md: 'center' }} 
          gap={1} 
        >
          <Text fontSize={{ base: "2xs", md: 'sm' }} whiteSpace="nowrap" color="text.t1/60" fontWeight="medium">
            {t('maxDrawdown')}
          </Text>
          <Text fontSize={{ base: "sm", md: 'xl' }} color="text.t1/80" fontWeight="semibold">
            {formatPercentageDisplay(userDetail?.maxDrawdown ?? 0, {
              signDisplay: 'auto',
            })}
          </Text>
        </Stack>
        <Stack
          width={{ base: "25%", md: '100%' }}
          direction={{ base: "column", md: 'row' }} 
          justify={{ base: "start", md: 'space-between' }}
          align={{ base: "center", md: 'center' }} 
          gap={1} 
        >
          <Text fontSize={{ base: "2xs", md: 'sm' }} color="text.t1/60" fontWeight="medium">
            {t('avgHold')}
          </Text>
          <Text fontSize={{ base: "sm", md: 'xl' }} color="text.t1/80" fontWeight="semibold">
            {formatHoldingTime(userDetail?.avgHold ?? 0)}
          </Text>
        </Stack>
        <Stack
          width={{ base: "25%", md: '100%' }}
          direction={{ base: "column", md: 'row' }} 
          justify={{ base: "start", md: 'space-between' }}
          align={{ base: "end", md: 'center' }} 
          gap={1} 
        >
          <Text fontSize={{ base: "2xs", md: 'sm' }} color="text.t1/60" fontWeight="medium">
            {t('maxHold')}
          </Text>
          <Text fontSize={{ base: "sm", md: 'xl' }} color="text.t1/80" fontWeight="semibold">
            {formatHoldingTime(userDetail?.maxHold ?? 0)}
          </Text>
        </Stack>
      </Stack>
    </VStack>
  );
}
